/**
 * Tasks Tab Component for Event Details Dialog
 * Manages tasks for a booking with add, complete, reopen, and delete functionality
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { CheckSquare, Plus, Trash2, ClipboardList } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TasksTab({ 
  booking, 
  tasks = [], 
  onTasksUpdate,
  formatDate,
  onGenerateTasks
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [newTask, setNewTask] = useState({ 
    name: '', 
    description: '', 
    due_date: '', 
    responsible: 'dj', 
    notify_client: false 
  });

  const handleAddTask = async () => {
    if (!newTask.name.trim() || !newTask.due_date) {
      toast.error('Task name and due date are required');
      return;
    }

    try {
      await axios.post(`${API}/bookings/${booking.id}/tasks`, newTask, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      setShowDialog(false);
      setNewTask({ name: '', description: '', due_date: '', responsible: 'dj', notify_client: false });
      onTasksUpdate();
      toast.success('Task added');
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      await axios.put(`${API}/bookings/${booking.id}/tasks/${taskId}/complete`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Task completed');
      onTasksUpdate();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleReopenTask = async (taskId) => {
    try {
      await axios.put(`${API}/bookings/${booking.id}/tasks/${taskId}/reopen`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Task reopened');
      onTasksUpdate();
    } catch (error) {
      toast.error('Failed to reopen task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    
    try {
      await axios.delete(`${API}/bookings/${booking.id}/tasks/${taskId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Task deleted');
      onTasksUpdate();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Event Tasks</h3>
          <p className="text-sm text-muted-foreground">
            {tasks.filter(t => t.status === 'completed').length} of {tasks.length} tasks completed
          </p>
        </div>
        <div className="flex gap-2">
          {onGenerateTasks && (
            <Button size="sm" variant="outline" onClick={onGenerateTasks} className="border-glass-border">
              Generate from Templates
            </Button>
          )}
          <Button size="sm" onClick={() => setShowDialog(true)} className="bg-neon-blue hover:bg-neon-blue/90">
            <Plus className="w-4 h-4 mr-1" />
            Add Task
          </Button>
        </div>
      </div>
      
      <div className="space-y-2">
        {tasks.length > 0 ? (
          tasks.map((task) => {
            const isOverdue = task.status !== 'completed' && task.due_date < today;
            const isDueToday = task.status !== 'completed' && task.due_date === today;
            
            return (
              <div
                key={task.id}
                className={`flex items-center gap-3 p-3 rounded-lg ${
                  task.status === 'completed' 
                    ? 'bg-muted/20 opacity-60' 
                    : isOverdue 
                      ? 'bg-red-500/10 border border-red-500/30' 
                      : isDueToday 
                        ? 'bg-orange-500/10 border border-orange-500/30'
                        : 'bg-muted/30'
                }`}
                data-testid={`task-item-${task.id}`}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className={`rounded-full ${task.status === 'completed' ? 'text-green-500' : 'text-muted-foreground'}`}
                  onClick={() => task.status === 'completed' ? handleReopenTask(task.id) : handleCompleteTask(task.id)}
                >
                  {task.status === 'completed' ? (
                    <CheckSquare className="w-5 h-5" />
                  ) : (
                    <div className={`w-5 h-5 border-2 rounded ${isOverdue ? 'border-red-500' : 'border-muted-foreground'}`} />
                  )}
                </Button>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${task.status === 'completed' ? 'line-through' : ''}`}>
                      {task.name}
                    </span>
                    {isOverdue && <span className="text-xs px-2 py-0.5 rounded-full bg-red-500 text-white">Overdue</span>}
                    {isDueToday && !isOverdue && <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500 text-white">Due Today</span>}
                    {task.notify_client && <span className="text-xs px-2 py-0.5 rounded-full bg-neon-blue/20 text-neon-blue">Notifies Client</span>}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                    <span>Due: {formatDate ? formatDate(task.due_date) : task.due_date}</span>
                    <span className="capitalize">{task.responsible === 'dj' ? 'DJ/Owner' : task.responsible}</span>
                    {task.description && <span className="truncate max-w-[200px]">{task.description}</span>}
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDeleteTask(task.id)}
                  className="text-muted-foreground hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No tasks for this event</p>
            <p className="text-sm mt-2">Click "Generate from Templates" to create tasks based on your task templates</p>
          </div>
        )}
      </div>
      
      {/* Add Task Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-card border border-border">
          <DialogHeader>
            <DialogTitle>Add Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Task Name *</Label>
              <Input
                value={newTask.name}
                onChange={(e) => setNewTask({ ...newTask, name: e.target.value })}
                placeholder="e.g., Confirm venue details"
                className="bg-muted/20 border-glass-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Optional description"
                className="bg-muted/20 border-glass-border"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Due Date *</Label>
                <Input
                  type="date"
                  value={newTask.due_date}
                  onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                  className="bg-muted/20 border-glass-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Responsible</Label>
                <Select value={newTask.responsible} onValueChange={(v) => setNewTask({ ...newTask, responsible: v })}>
                  <SelectTrigger className="bg-muted/20 border-glass-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-glass-border">
                    <SelectItem value="dj">DJ / Owner</SelectItem>
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={newTask.notify_client}
                onCheckedChange={(v) => setNewTask({ ...newTask, notify_client: v })}
              />
              <Label className="text-sm">Notify client when task is due</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="border-glass-border">
              Cancel
            </Button>
            <Button onClick={handleAddTask} className="bg-neon-blue hover:bg-neon-blue/90">
              Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
